import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import PasswordStrength from "../components/PasswordStrength";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  // Prefill demo credentials only when explicitly enabled via env — NODE_ENV
  // is baked at build time and CRA dev builds (used in preview) always look
  // like 'development', which would leak creds. Instead, require an opt-in.
  const showDemo = process.env.REACT_APP_SHOW_DEMO_CREDS === "true";
  const [form, setForm] = useState({
    email: showDemo ? "felecia@realtoros.demo" : "",
    password: showDemo ? "PhoenixAgent2026!" : "",
    full_name: "",
    phone: "",
    confirm: "",
  });
  const referralCode = params.get("ref");

  useEffect(() => {
    if (referralCode) {
      setMode("register");
      setForm(f => ({ ...f, email: "", password: "", confirm: "" }));
    }
  }, [referralCode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (mode === "register") {
      if (!form.email || !form.full_name) return toast.error("Name and email are required");
      if (form.password.length < 8) return toast.error("Password must be at least 8 characters");
      if (form.password !== form.confirm) return toast.error("Passwords do not match");
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        nav("/");
      } else {
        await register({ email: form.email, password: form.password, full_name: form.full_name || "Agent", phone: form.phone, brand_color: "#1F6FEB", referral_code: referralCode });
        localStorage.removeItem("realtor_os_onboarded");
        nav("/onboarding");
      }
    } catch (err) {
      const raw = err?.response?.data?.detail || err?.response?.data?.error_description
        || err?.response?.data?.message || err?.message || "Something went wrong";
      const rawLower = String(raw).toLowerCase();
      const status = err?.response?.status;
      const isBadCreds = mode === "login" && (
        status === 400 || status === 401
        || rawLower.includes("invalid login credentials")
        || rawLower.includes("invalid_credentials")
        || rawLower.includes("invalid email or password")
      );
      if (isBadCreds) {
        const msg = "That email and password don't match. Check both and try again.";
        setLoginError(msg);
        toast.error(msg);
      } else {
        toast.error(raw);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative lg:grid lg:grid-cols-2 lg:min-h-screen">
      {/*
        Both mobile and desktop share the same luxury photo background —
        the desktop card floats over it exactly like the mobile card,
        with a subtle backdrop-blur so the estate stays visible but soft.
      */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/30 to-slate-950/85" />
      </div>

      {/* DESKTOP-only hero column — decorative overlay on top of the shared
          background image, giving the left half its dedicated brand copy. */}
      <div className="hidden lg:flex lg:flex-col relative overflow-hidden z-10">
        <div className="relative z-10 flex items-center gap-2.5 px-12 pt-12 text-white">
          <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-white/20" />
          <span className="font-heading text-xl font-semibold tracking-tight">iKEY</span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-[38px] leading-[1.05] font-bold tracking-tight max-w-lg drop-shadow-lg">
            iKEY: The Only Key You Need
            <span className="block mt-1 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              from Lead to Sold.
            </span>
          </h2>
        </div>
      </div>

      {/* ── Login pane ─────────────────────────────────────────────── */}
      <div className="relative z-10 min-h-screen lg:min-h-0 flex flex-col lg:items-center lg:justify-center px-6 sm:px-10 lg:px-16 py-8 lg:py-16">
        {/* Mobile-only slogan at the top */}
        <div className="lg:hidden pt-6 pb-8 text-center text-white">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-white/25" />
            <span className="font-heading text-xl font-semibold tracking-tight drop-shadow">iKEY</span>
          </div>
          <h2 className="font-heading text-[26px] leading-[1.1] font-bold tracking-tight px-2 drop-shadow-lg">
            iKEY: The Only Key You Need
            <span className="block mt-1 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">
              from Lead to Sold.
            </span>
          </h2>
        </div>

        {/* Login card — glass on every screen size. `bg-white/60` gives ~60%
            opacity so the photo shows through; `backdrop-blur-xl` softens
            it so text stays legible. Inputs & buttons keep solid backgrounds. */}
        <div className="flex-1 lg:flex-none flex items-center justify-center w-full">
          <div
            className="w-full max-w-sm rounded-2xl bg-white/30 backdrop-blur-xl shadow-2xl ring-1 ring-white/30 p-6 sm:p-7"
            data-testid="login-card"
          >
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-white drop-shadow-md">{mode === "login" ? "Welcome back" : "Create your workspace"}</h1>
            <p className="mt-2 text-sm text-white/90 drop-shadow">{mode === "login" ? "Sign in to continue where you left off." : "Start on the permanently free CRM — 1–3 clients, no time limit. Upgrade any time to add more."}</p>

            {referralCode && mode === "register" && (
              <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="referral-banner">
                You were invited with code <span className="font-mono font-semibold">{referralCode}</span> — your referrer earns a free month when you start a subscription.
              </div>
            )}

            <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4">
              {mode === "register" && (
                <div>
                  <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Full name</label>
                  <input required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-brand" data-testid="register-name-input" />
                </div>
              )}
              {mode === "register" && (
                <div>
                  <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Phone</label>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value})}
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-brand"
                    data-testid="register-phone-input"
                  />
                </div>
              )}
              {mode === "login" && (
                <div>
                  <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Email</label>
                <input type="email" required value={form.email} onChange={e => { setForm({...form, email: e.target.value}); if (loginError) setLoginError(""); }} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-brand" data-testid="login-email-input" />
              </div>
              )}
              {mode === "register" && (
                <div>
                  <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Email</label>
                  <input type="email" required autoComplete="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-brand" data-testid="register-email-input" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Password</label>
                <div className="mt-1 relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    value={form.password}
                    onChange={e => { setForm({...form, password: e.target.value}); if (loginError) setLoginError(""); }}
                    className="w-full rounded-md border border-slate-300 bg-white pl-3 pr-10 py-2 text-sm text-slate-900 focus-brand"
                    data-testid={mode === "register" ? "register-password-input" : "login-password-input"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-800"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {mode === "register" && <PasswordStrength password={form.password} />}
              </div>
              {mode === "register" && (
                <div>
                  <label className="text-xs font-semibold text-white uppercase tracking-wider drop-shadow">Confirm password</label>
                  <input type="password" required autoComplete="new-password" value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus-brand" data-testid="confirm-password-input" />
                  {form.confirm && form.password !== form.confirm && <span className="mt-1 block text-xs text-rose-600">Passwords don't match</span>}
                </div>
              )}
              {mode === "login" && (
                <div className="text-right">
                  <Link to="/reset" className="text-xs font-medium text-white/90 hover:text-white drop-shadow underline-offset-4 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
              )}
              {mode === "login" && loginError && (
                <div
                  role="alert"
                  className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex items-start gap-2"
                  data-testid="login-error"
                >
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{loginError} <Link to="/reset" className="font-semibold underline hover:text-rose-900">Reset password</Link></span>
                </div>
              )}
              <button type="submit" disabled={busy} className="w-full rounded-md py-2.5 text-sm font-semibold text-white shadow-md disabled:opacity-60" style={{ background: "#1F6FEB" }} data-testid="login-submit-btn">
                {busy ? "Working…" : (mode === "login" ? "Sign in" : "Create workspace")}
              </button>
            </form>

            <div className="mt-6 text-sm text-white/90 drop-shadow text-center">
              {mode === "login" ? (
                <>New to iKEY? <button className="font-semibold text-white underline underline-offset-4 hover:text-amber-200" onClick={() => setMode("register")} data-testid="switch-to-register">Create an account</button></>
              ) : (
                <>Already have an account? <button className="font-semibold text-white underline underline-offset-4 hover:text-amber-200" onClick={() => setMode("login")} data-testid="switch-to-login">Sign In</button></>
              )}
            </div>

            {/* Legal footer — high-contrast on the tinted photo backdrop.
                target="_blank" opens Terms/Privacy in a new tab so an
                agent mid-signup doesn't lose their form state, and
                rel="noopener noreferrer" is the safe default for links
                that leave a form context. */}
            <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-white/80 drop-shadow" data-testid="login-legal-footer">
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline underline-offset-4"
                data-testid="login-terms-link"
              >
                Terms of Service
              </Link>
              <span aria-hidden="true">·</span>
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white underline underline-offset-4"
                data-testid="login-privacy-link"
              >
                Privacy Policy
              </Link>
            </div>

            {showDemo && (
              <div className="mt-8 rounded-md border border-slate-200 bg-white/90 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-600">Demo account</p>
                <p className="mt-1 text-sm text-slate-800">felecia@realtoros.demo · PhoenixAgent2026!</p>
                <p className="mt-1 text-xs text-slate-600">Phoenix-area agent with 10 seeded leads across every stage.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
